/**
 * allocationModel — the pure logic behind the per-pair allocation editor
 * (SUPERBETA-6). No React, no network: every rule here is testable on its own.
 *
 * Ground truth is ENGINE_CONTRACTS_FOR_FRONTEND_2026-08-05.md, sections 1 and
 * 1b. Three of its rules shape this file:
 *
 *   1. Allocation shape is `{ pair, pct }`, pct 0–100, **sum ≤ 100**, remainder
 *      is implicit cash. The engine rejects the whole patch on violation and
 *      never partially applies it — so the UI must block the save rather than
 *      send something it knows will be thrown away.
 *   2. **Engine computes the floors, the frontend renders them.** The minimum
 *      viable allocation depends on gTrade's live per-pair minimum, the
 *      member's leverage and a moving balance; a frontend copy of that maths
 *      drifts from what the engine enforces. We compare the member's `pct` to
 *      the engine's `minPct` and nothing more. The one multiplication below
 *      (pct% of balanceUsd) exists only to restate the member's OWN input in
 *      dollars inside the warning copy the contract specifies.
 *   3. **Absence means "floors unknown".** With no `sizingFloors` on /status we
 *      render no floor marks and no below-minimum warnings. We never invent a
 *      number to fill the gap.
 */

/* Engine wire key for a display pair: "BTC/USD" -> "BTCUSD" (matches the
   backend's normalizePairName — uppercase, alphanumerics only). Floors and
   allocations arrive keyed this way. Owned by services/engineApi.js and
   re-exported here so the tab's long-standing import keeps working — there is
   exactly one copy of this rule. */
import { normalizePairKey } from "../../services/engineApi";

export const wireKey = normalizePairKey;

const num = (n) => (Number.isFinite(Number(n)) ? Number(n) : null);

/* ── sizing floors (contract §1b) ───────────────────────────────────────── */

/**
 * Read /status.sizingFloors into a lookup.
 * @returns {{known: boolean, balanceUsd: number|null, computedAtMs: number|null,
 *            byPair: Object<string, {pair, minPositionUsd, leverage,
 *                                    minCollateralUsd, minPct}>}}
 * `known: false` (the shape absent or unusable) is the "floors unknown" state —
 * callers must render nothing rather than guess.
 */
export const readSizingFloors = (status) => {
  const raw = status?.sizingFloors;
  const list = Array.isArray(raw?.floors) ? raw.floors : null;
  if (!list) return { known: false, balanceUsd: null, computedAtMs: null, byPair: {} };

  const byPair = {};
  list.forEach((f) => {
    const key = wireKey(f?.pair);
    const minPct = num(f?.minPct);
    if (!key || minPct === null) return; // an entry we can't compare against is not a floor
    byPair[key] = {
      pair: f.pair,
      minPct,
      minPositionUsd: num(f?.minPositionUsd),
      minCollateralUsd: num(f?.minCollateralUsd),
      leverage: num(f?.leverage),
    };
  });

  return {
    known: Object.keys(byPair).length > 0,
    balanceUsd: num(raw?.balanceUsd),
    computedAtMs: num(raw?.computedAtMs),
    byPair,
  };
};

/** The floor for one display pair, or null when the engine didn't send one. */
export const floorFor = (floors, pair) => floors?.byPair?.[wireKey(pair)] || null;

/**
 * Is this allocation under the pair's minimum viable percentage?
 * A pct of 0 is "not allocated", not "below the floor" — the member has simply
 * turned the pair off, and no trade will be sized for it at all.
 */
export const isBelowFloor = (pct, floor) => {
  const p = num(pct);
  if (!floor || p === null || p <= 0) return false;
  return p < floor.minPct;
};

/* Dollar formatting for the warning copy — cents matter here, the whole point
   of the floor is that 25% misses BTC's minimum by $0.88. */
const usd = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/** The one-line amber badge. Operational fact, no advice, no remedy suggested. */
export const belowFloorSummary = (floor) =>
  `Below this pair's ${floor.minPct}% minimum — trades would be rejected, not placed.`;

/**
 * The detail line, per contract rule 3: state the mechanics, never suggest
 * raising leverage as the remedy. Falls back to the percentages alone when the
 * engine didn't send a balance to restate the member's input against.
 */
export const belowFloorDetail = (pct, floor, balanceUsd) => {
  const p = num(pct) ?? 0;
  const tail =
    floor.minPositionUsd != null && floor.leverage != null
      ? ` (${usd(floor.minCollateralUsd)} collateral — gTrade's ${usd(
          floor.minPositionUsd
        )} minimum position at ${floor.leverage}x).`
      : ".";
  if (balanceUsd == null) {
    return `Your ${p}% is under this pair's ${floor.minPct}% minimum${tail} Trades for this pair will be rejected, not resized.`;
  }
  /* pct% of the member's own balance — restating their input, not re-deriving
     the engine's floor. See the header note on rule 2. */
  const theirs = (balanceUsd * p) / 100;
  return `At your current balance, ${p}% of ${usd(balanceUsd)} is ${usd(theirs)} — under this pair's ${
    floor.minPct
  }% minimum${tail} Trades for this pair will be rejected, not resized.`;
};

/* ── the sum ≤ 100 invariant (contract §1, Phase 1) ─────────────────────── */

/** Total of every non-cash allocation, unclamped — an over-allocation must be
    visible as one rather than silently rewritten. */
export const investedTotal = (rows) =>
  (rows || []).reduce((s, r) => s + (Number(r?.allocationPct) || 0), 0);

/** How far past 100% the member is, 0 when they're within budget. */
export const overAllocatedBy = (rows) => Math.max(0, investedTotal(rows) - 100);

/* ── the echo check (the silent-drop bug this issue exists to prevent) ──── */

/**
 * Did the engine actually store what we sent?
 *
 * The contract's finding 1: `/config` used to accept unknown fields, return 200
 * and discard them, and the UI reported success because it echoed LOCAL state.
 * So after every save we refetch and compare the engine's own answer to the
 * payload — the member is shown the echo, and an unconfirmed save says so.
 *
 * @param sent   the allocations payload we posted: { BTCUSD: {pct, leverage?} }
 * @param echoed status.riskConfig.allocations from the refetch
 * @returns {{confirmed: boolean, missing: string[], mismatched: string[], echoAbsent: boolean}}
 */
export const compareAllocationEcho = (sent, echoed) => {
  const payload = sent || {};
  const echoAbsent = !echoed || typeof echoed !== "object";
  const back = echoAbsent ? {} : echoed;

  const missing = [];
  const mismatched = [];
  Object.keys(payload).forEach((key) => {
    const want = Number(payload[key]?.pct) || 0;
    const got = back[key];
    if (got == null) {
      missing.push(key);
    } else if ((Number(got.pct) || 0) !== want) {
      mismatched.push(key);
    }
  });

  /* A pair we cleared (or never sent) that comes back with an allocation means
     the patch didn't land either — the echo is not what we asked for. */
  Object.keys(back).forEach((key) => {
    if (!(key in payload) && (Number(back[key]?.pct) || 0) > 0) mismatched.push(key);
  });

  return {
    confirmed: !echoAbsent && missing.length === 0 && mismatched.length === 0,
    missing,
    mismatched,
    echoAbsent,
  };
};
